import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;

const mergeUnique = (base, extra) => {
  const seen = new Set(base.map((o) => o.value));
  return [...base, ...extra.filter((o) => !seen.has(o.value))];
};

/** پاسخ‌های { data, pagination } با یا بدون لایه data اضافه */
const unwrap = (res) => {
  const root = res?.data && !Array.isArray(res.data) ? res.data : res;
  const items = Array.isArray(root?.data) ? root.data : Array.isArray(res?.data) ? res.data : [];
  const pagination = root?.pagination || res?.pagination || {};
  return { items, hasMore: Boolean(pagination.has_more) };
};

/**
 * گزینه‌های Select با جستجوی سمت سرور و اسکرول بی‌نهایت.
 * - fetcher({ search, page, per_page }) و fetcher({ ids }) باید پاسخ { data, pagination: { has_more } } بدهند
 * - تا زمان باز شدن dropdown چیزی بارگذاری نمی‌شود
 * - برچسب مقدار(های) انتخاب‌شده که در صفحه فعلی نیستند با ids[] گرفته می‌شود
 *
 * `selectProps` را مستقیم روی antd Select / AppSelect پخش کنید.
 */
export default function useRemoteOptions({
  fetcher,
  toOption,
  selectedValue,
  pageSize = DEFAULT_PAGE_SIZE,
  extraParams,
} = {}) {
  const [options, setOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const fetcherRef = useRef(fetcher);
  const toOptionRef = useRef(toOption);
  fetcherRef.current = fetcher;
  toOptionRef.current = toOption;

  const extraKey = JSON.stringify(extraParams || {});
  const pageRef = useRef(0);
  const searchRef = useRef('');
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);
  const loadedOnceRef = useRef(false);
  const loadingRef = useRef(false);

  const fetchPage = useCallback(
    async (page, search) => {
      const requestId = ++requestIdRef.current;
      loadingRef.current = true;
      setLoading(true);
      try {
        const res = await fetcherRef.current({ ...JSON.parse(extraKey), search, page, per_page: pageSize });
        if (requestId !== requestIdRef.current) return;
        const { items, hasMore: more } = unwrap(res);
        const mapped = items.map(toOptionRef.current);
        pageRef.current = page;
        setHasMore(more);
        setOptions((prev) => (page === 1 ? mapped : mergeUnique(prev, mapped)));
      } catch (err) {
        if (requestId === requestIdRef.current) {
          console.warn('Could not load select options:', err);
          setHasMore(false);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [pageSize, extraKey],
  );

  // پارامترهای اضافه عوض شد → لیست از نو
  useEffect(() => {
    if (!loadedOnceRef.current) return;
    fetchPage(1, searchRef.current);
  }, [fetchPage]);

  const onOpenChange = useCallback(
    (open) => {
      if (!open || loadedOnceRef.current) return;
      loadedOnceRef.current = true;
      fetchPage(1, searchRef.current);
    },
    [fetchPage],
  );

  const onSearch = useCallback(
    (value) => {
      const term = (value || '').trim();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (term === searchRef.current && loadedOnceRef.current) return;
        searchRef.current = term;
        loadedOnceRef.current = true;
        fetchPage(1, term);
      }, SEARCH_DEBOUNCE_MS);
    },
    [fetchPage],
  );

  const onPopupScroll = useCallback(
    (e) => {
      const target = e.target;
      if (!hasMore || loadingRef.current) return;
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
        fetchPage(pageRef.current + 1, searchRef.current);
      }
    },
    [hasMore, fetchPage],
  );

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  const selectedKey = []
    .concat(selectedValue ?? [])
    .map(Number)
    .filter((v) => Number.isFinite(v) && v > 0)
    .sort((a, b) => a - b)
    .join(',');

  useEffect(() => {
    if (!selectedKey) return;
    const ids = selectedKey.split(',').map(Number);
    const known = new Set([...options, ...selectedOptions].map((o) => o.value));
    const missing = ids.filter((id) => !known.has(id));
    if (!missing.length) return;
    let cancelled = false;
    fetcherRef.current({ ids: missing })
      .then((res) => {
        if (cancelled) return;
        const mapped = unwrap(res).items.map(toOptionRef.current);
        setSelectedOptions((prev) => mergeUnique(prev, mapped));
      })
      .catch((err) => console.warn('Could not resolve selected option labels:', err));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  /** افزودن گزینه‌ای که از جای دیگری معلوم است (مثلاً preset یا رکورد در حال ویرایش) */
  const addOptions = useCallback((extra) => {
    const list = [].concat(extra || []).filter((o) => o?.value != null);
    if (list.length) setSelectedOptions((prev) => mergeUnique(prev, list));
  }, []);

  const mergedOptions = useMemo(() => mergeUnique(options, selectedOptions), [options, selectedOptions]);

  return {
    options: mergedOptions,
    loading,
    hasMore,
    addOptions,
    selectProps: {
      showSearch: true,
      filterOption: false,
      onSearch,
      onPopupScroll,
      onOpenChange,
      loading,
      notFoundContent: loading ? 'در حال جستجو...' : 'موردی یافت نشد',
    },
  };
}
